// ─── API Envelope Types ───
import type { TaskWritableField, ViewerTier } from '../policy/field-policy';

/**
 * Viewer-permission block carried in response `meta` (Batch E, ADR 0018).
 * Returned by the list-bearing task endpoints (list / board / gantt / getById) so the
 * client can gate field editing at the SAME tier the server enforces — the frontend
 * Task carries no workspaceId/spaceId, so it cannot re-derive the effective role.
 * The per-task assignee check stays client-side (`task.assignments` is in the payload).
 */
export interface ViewerMeta {
  /** The caller's effective field-policy tier for the scope this response was resolved against. */
  tier: ViewerTier;
  /**
   * Whether this caller may create a task in this scope at all (GHE #883). Since
   * `CreateTaskSchema.clientId` is mandatory, that is the same question as "may they tag any
   * client here" — superadmin, a manager of this area, or the RT of at least one client.
   *
   * Carried here for the same reason as `tier`: the frontend Task has no workspaceId/spaceId and
   * cannot re-derive it, and a UI that guesses would hide a button from someone allowed to use it
   * or show one that 403s. Resolved server-side from the SAME predicate the write uses.
   *
   * ⚠️ Scope-dependent — it is an answer about THIS list, not about the user. Do not cache it
   * globally: a manager of one area is not a manager of another.
   */
  canCreateTask: boolean;
  /**
   * The clients this caller is the **RT (TECHNICAL_LEAD)** of — GHE #902.
   *
   * The RT carve-out lets an RT edit a few otherwise manage-tier fields on tasks of THEIR
   * client while nobody is assigned. That answer is per-TASK (one list holds tasks of many
   * clients), so it cannot be folded into `tier`, which is one value for the whole response.
   * Carrying the client-id SET instead of a per-row boolean keeps the payload flat and lets
   * the client answer the question for any task in the response: is `task.clientId` in here,
   * and does `task.assignments` have length 0?
   *
   * ⚠️ The unassigned half is deliberately computed CLIENT-side, because the frontend `Task`
   * carries its full `assignments` array — unlike the api's update path, whose task load is
   * filtered to the caller's own assignment and therefore cannot answer "assigned to anyone".
   *
   * ⚠️ Empty for a manager tier even when that person IS an RT somewhere: the manager arm
   * already permits every field, so the carve-out would change nothing and the lookup is
   * skipped. Do not read this as "not an RT".
   *
   * Absent (older api) ⇒ treat as empty ⇒ no carve-out ⇒ the pre-#902 behaviour. That is the
   * safe direction here, and unlike #883's `?? true` it removes nothing anyone already had:
   * it degrades to today's rules rather than blacking a control out.
   */
  technicalLeadClientIds?: string[];
}

/**
 * Common optional response meta. Endpoint-specific metas extend this
 * (e.g. `PaginationMeta & ResponseMeta` on list endpoints, or the web-side
 * Gantt meta adding `dependencies`).
 */
export interface ResponseMeta {
  /** Viewer permission context (ADR 0018) — present on the four task read endpoints. */
  viewer?: ViewerMeta;
}

export interface ApiResponse<T> {
  data: T;
  /**
   * Optional envelope meta. Present on task read endpoints that carry the
   * viewer tier (board grouped view, task getById); absent elsewhere.
   */
  meta?: ResponseMeta;
}

export interface ApiListResponse<T> {
  data: T[];
  meta: {
    nextCursor: string | null;
    /**
     * GHE #1291 — cuántas filas hay en TOTAL tras acotar, no cuántas trae esta página.
     *
     * Lo puso el selector de personas por área, cuya cabecera dice «Backend · 3 personas»: con
     * `data.length` diría «10 personas» en un área de treinta, porque la página son diez. Sólo
     * lo rellenan los listados que lo calculan (hoy `GET /users`); ausente significa «este
     * endpoint no lo responde», nunca «cero».
     */
    total?: number;
  } & ResponseMeta;
}

/** RFC 7807 Problem Details */
export interface ApiError {
  type: string;
  title: string;
  status: number;
  detail: string;
  instance: string;
}

/**
 * RFC 7807 `type` URI for the field-level-permission 403 (ADR 0018).
 * Shared constant so the api emitter and the web handler can never drift.
 */
export const FIELD_FORBIDDEN_PROBLEM_TYPE = 'https://a8planner/problems/field-forbidden';

/**
 * RFC 7807 `type` URI for an optimistic-locking failure (#269). Shared constant so the
 * api emitter and the web handler can never drift.
 *
 * A 409 alone does NOT mean a version conflict — a unique-name collision, a status that
 * still has tasks referencing it, and an immutable published questionnaire all 409 too.
 * The web client treats only THIS type as "someone else changed it"; every other 409
 * renders the server's own `detail`, which is already specific and actionable.
 */
export const VERSION_CONFLICT_PROBLEM_TYPE = 'https://a8planner/problems/version-conflict';

/**
 * The field-level-permission 403 problem (ADR 0018). Emitted by the task write
 * chokepoints when the caller's tier may not edit one or more of the changed fields.
 * The `fields` extension member lets the client show a precise message and re-sync
 * (drop the offending local edits + refetch) instead of a blanket "forbidden".
 */
export interface FieldForbiddenProblem extends ApiError {
  type: typeof FIELD_FORBIDDEN_PROBLEM_TYPE;
  status: 403;
  /** The offending fields (subset of the attempted change), in stable policy order. */
  fields: TaskWritableField[];
}

/** Type guard: is this RFC 7807 problem the field-forbidden 403? */
export function isFieldForbiddenProblem(problem: unknown): problem is FieldForbiddenProblem {
  if (typeof problem !== 'object' || problem === null) return false;
  const p = problem as Record<string, unknown>;
  return (
    p['type'] === FIELD_FORBIDDEN_PROBLEM_TYPE && p['status'] === 403 && Array.isArray(p['fields'])
  );
}

export interface PaginationParams {
  cursor?: string;
  limit?: number;
}
